import { FunctionComponent, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Switch,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import useGiftCardsStore from '../../stores/giftCards'

const GiftCardConfigView: FunctionComponent = () => {
  const { id: membershipId } = useParams<{ id: string }>()
  const { products, supportedMonthOptions, fetchGiftCardProducts, createGiftCardProduct, updateGiftCardProduct, removeGiftCardProduct } =
    useGiftCardsStore()

  const [loading, setLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    if (!membershipId) return
    setLoading(true)
    fetchGiftCardProducts(membershipId)
      .catch(() => toast.error('Failed to load gift card options.'))
      .finally(() => setLoading(false))
  }, [membershipId]) // eslint-disable-line react-hooks/exhaustive-deps

  const membershipProducts = membershipId ? (products[membershipId] || []) : []
  const monthOptions = membershipId ? (supportedMonthOptions[membershipId] || []) : []

  const handleToggle = async (id: number, currentStatus: string) => {
    if (!membershipId) return
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    try {
      await updateGiftCardProduct(membershipId, id, newStatus)
      toast.success(`Gift card option ${newStatus === 'active' ? 'activated' : 'deactivated'}.`)
    } catch (e: any) {
      toast.error(e.message || 'Failed to update gift card option.')
    }
  }

  const effectiveMonths = selectedMonths ?? monthOptions[0] ?? null

  const handleAdd = async () => {
    if (!membershipId || effectiveMonths === null) return
    setAdding(true)
    try {
      await createGiftCardProduct(membershipId, effectiveMonths)
      toast.success('Gift card option added.')
      setShowAddForm(false)
      setSelectedMonths(null)
    } catch (e: any) {
      toast.error(e.message || 'Failed to add gift card option.')
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (id: number) => {
    if (!membershipId) return
    try {
      await removeGiftCardProduct(membershipId, id)
      toast.success('Gift card option removed.')
    } catch (e: any) {
      toast.error(e.message || 'Failed to remove gift card option.')
    }
  }

  return (
    <Box p={4}>
      {/* Header row */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6">Gift Card Options</Typography>
        {monthOptions.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setShowAddForm((prev) => !prev)}
          >
            {showAddForm ? 'Cancel' : 'Add Option'}
          </Button>
        )}
      </Box>

      {/* Add form */}
      {showAddForm && (
        <Box
          display="flex"
          alignItems="center"
          mb={3}
          p={2}
          style={{ gap: 12, background: '#f5f5f5', borderRadius: 4 }}
        >
          <Typography variant="body2">Months:</Typography>
          <FormControl variant="outlined" size="small">
            <Select
              value={selectedMonths ?? monthOptions[0] ?? ''}
              onChange={(e) => setSelectedMonths(e.target.value as number)}
            >
              {monthOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {m >= 1200 ? 'Lifetime' : `${m} ${m === 1 ? 'Month' : 'Months'}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAdd}
            disabled={adding}
          >
            {adding ? <CircularProgress size={16} /> : 'Add'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowAddForm(false)
              setSelectedMonths(null)
            }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && monthOptions.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          Gift cards are not available for this membership type. Only memberships with monthly or annual pricing can have gift card options.
        </Typography>
      )}
      {!loading && monthOptions.length > 0 && membershipProducts.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No gift card options configured. Add one to let patrons gift this membership.
        </Typography>
      )}

      {/* Product rows */}
      {!loading &&
        membershipProducts.map((product) => {
          const isActive = product.status === 'active'
          const hasPurchases = product.purchasedCount > 0
          const priceDisplay = product.grandTotal != null
            ? `$${(product.grandTotal / 100).toFixed(2)}`
            : '—'

          return (
            <Box
              key={product.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              mb={1}
              style={{ border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff' }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Typography variant="body1">
                  {product.quantityText || `${product.numberOfMonths} Month Gift Card`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {priceDisplay}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Switch
                  checked={isActive}
                  onChange={() => handleToggle(product.id, product.status)}
                  color="primary"
                  size="small"
                />
                <Typography variant="body2" color="textSecondary">
                  {isActive ? 'Active' : 'Inactive'}
                </Typography>

                {hasPurchases ? (
                  <Tooltip title="Cannot remove — gift cards have been purchased. Use the toggle to hide this option instead.">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled
                        style={{ marginLeft: 8 }}
                      >
                        Remove
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ marginLeft: 8 }}
                    onClick={() => handleRemove(product.id)}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
          )
        })}
    </Box>
  )
}

export default GiftCardConfigView
